import type { ElementType } from 'react'
import { useI18n, type MessageKey, type MessageVars } from '@/i18n'
import { cn } from '@/lib/cn'

export interface BilingualLabelProps {
  /** Dictionary key — both languages come from it. */
  k?: MessageKey
  vars?: MessageVars
  /** Or literal strings (e.g. data from the server that is already bilingual). */
  hi?: string
  en?: string
  /** Kept for existing call sites; only one language is shown either way. */
  layout?: 'inline' | 'stacked'
  as?: ElementType
  className?: string
  secondaryClassName?: string
}

/**
 * A label in the language the toggle picked — one language at a time, never "हिंदी · English".
 * Falls back to the other language only when the picked one is missing (server data).
 * `layout` and `secondaryClassName` are kept so existing call sites stay unchanged.
 */
export function BilingualLabel({
  k,
  vars,
  hi: hiText,
  en: enText,
  layout = 'inline',
  as: Tag = 'span',
  className,
}: BilingualLabelProps) {
  const { locale, pair } = useI18n()
  const texts = k ? pair(k, vars) : { hi: hiText ?? '', en: enText ?? '' }
  const picked = locale === 'hi' ? texts.hi : texts.en
  const text = picked || (locale === 'hi' ? texts.en : texts.hi)
  const lang = picked ? locale : locale === 'hi' ? 'en' : 'hi'
  return (
    <Tag className={cn(layout === 'stacked' && 'flex flex-col', className)} lang={lang}>
      {text}
    </Tag>
  )
}
